import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, ButtonGroup, withAnimatedNavigationHeader } from 'design-system';
import Button, { HEIGHT, HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import useStyles from 'hooks/useStyles';
import { ThrowableFetchError } from 'hooks';
import { useAppSelector } from 'store/hooks';
import useAppRating from 'hooks/useAppRating';
import usePasscodeModal from 'hooks/usePasscodeModal';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import { formatDate } from '../utils';
import { useStockMeta } from '../hooks';
import Portfolio from '../components/Portfolio';
import useOrderCache from '../hooks/useOrderCache';
import useOrderCancel from '../hooks/useOrderCancel';
import { selectActiveTradingAccountId, selectAutoInvestPlanForAccount, selectHasAutoInvest, selectIsTradingAccountAlpaca, selectTradingAccount, } from '../reducers/selectors';
import { SellOrderType } from '../types';
import Receipt from '../components/Receipt';
import { blockInvestActionOnWeb } from '../webGate';
import { isWeb } from '../../../constants';
const strings = {
    cancel: 'Cancel this order',
    close: 'Close',
    subtitle: {
        queued: 'Your order is placed. We will execute it as soon as market opens',
        cancelled: 'Your order is cancelled',
        failed: 'We were unable to process your order',
    },
    recurringOrder: 'Make this a recurring order',
    makeItRecurring: 'Make it recurring',
    marketOpensAt: ' Market opens at ',
    cancelling: 'cancelling...',
    cancellingSubtitle: 'Processing, please wait',
    biometryDescription: 'You are about to cancel your order',
    passcodeTitle: 'Enter Your Pin Code',
};
const OrderScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const order = route.params?.order;
    const activity = route.params?.activity;
    const onCancelSuccess = route.params?.onCancel;
    const marketOpeningInfo = route.params?.marketOpeningInfo;
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const accountType = tradingAccount?.account.accountType;
    const hasFetchedAutoinvestPlans = useAppSelector((state) => !!state.invest.autoInvestPlans);
    const autoInvestPlan = useAppSelector((state) => selectAutoInvestPlanForAccount(state, tradingAccount?.account.id));
    const isAssetInAutoinvest = autoInvestPlan?.allocations.some((a) => a.assetId === order.assetId);
    const rateApp = useAppRating();
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const stock = useStockMeta(order.assetId);
    const hasAutoInvest = useAppSelector(selectHasAutoInvest);
    // we need this "fetched order" because sometimes this screen is opened with insufficient data for UI
    // that’s why we need to fetch that missing data
    // this hook will also update it for us every other second
    // it’s useful for case when order is pending
    const fetchedOrder = useOrderCache(activity ? activity.id : order.id, tradingAccountId);
    const [latestOrder, setLatestOrder] = useState(() => {
        if (fetchedOrder) {
            return fetchedOrder;
        }
        return order;
    });
    const mountedRef = useRef(false);
    useEffect(() => {
        if (mountedRef.current) {
            if (fetchedOrder) {
                setLatestOrder(fetchedOrder);
            }
        }
        else {
            mountedRef.current = true;
        }
    }, [fetchedOrder]);
    const [cancelOrder, cancelling] = useOrderCancel();
    const [getPin] = usePasscodeModal();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    useEffect(() => {
        if (fetchedOrder) {
            if (order.status !== 'cancelled' && fetchedOrder.status === 'cancelled') {
                Portfolio.update();
            }
            else if (order.status !== 'completed' && fetchedOrder.status === 'completed') {
                Portfolio.update();
            }
        }
    }, [order, fetchedOrder]);
    const onCancel = useCallback(async (forcePinAuthentication) => {
        if (scrambled)
            return;
        const onConfirm = async (pin) => {
            const orderType = 'orderType' in order ? order.orderType : undefined;
            if (orderType && tradingAccountId) {
                await cancelOrder(order.id, () => {
                    const newStatus = 'cancelled';
                    const updatedOrder = { ...order, status: newStatus };
                    Portfolio.update();
                    if (onCancelSuccess) {
                        // typecast makes sense here, because order comes as a partial
                        // TradingOrder only when this screen is shown for a cancelled
                        // activity. And this callback never gets called for cancelled
                        // activity, because the order for that activity is already cancelled
                        onCancelSuccess(updatedOrder);
                    }
                }, pin, stock?.id, orderType, tradingAccountId);
            }
        };
        try {
            const pin = await getPin({
                title: strings.passcodeTitle,
                biometryDescription: strings.biometryDescription,
                forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
            });
            try {
                await onConfirm(pin);
            }
            catch (error) {
                // Other errors are handled in cancelOrder
                const fetchError = error instanceof ThrowableFetchError ? error : undefined;
                if (fetchError?.fetchError.errorType === 'PinError') {
                    onCancel(true);
                }
            }
        }
        catch {
            // user changed his mind, do nothing
        }
    }, [scrambled, order, tradingAccountId, cancelOrder, stock?.id, onCancelSuccess, getPin]);
    const onDone = useCallback(() => {
        navigation.goBack();
        if (latestOrder.operation === 'buy' && (latestOrder.status === 'completed' || latestOrder.status === 'queued')) {
            rateApp();
        }
    }, [latestOrder.operation, latestOrder.status, rateApp]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPress={onDone}/>,
        });
    }, [onDone]);
    const title = useMemo(() => {
        const name = latestOrder.symbol || stock?.name;
        let prefix = '';
        if ('orderType' in latestOrder && (latestOrder.status === 'pending' || latestOrder.status === 'queued')) {
            if (latestOrder.orderType === SellOrderType.LIMIT) {
                prefix = `Limit ${latestOrder.operation}`;
            }
            else if (latestOrder.orderType === SellOrderType.STOP) {
                prefix = `Stop ${latestOrder.operation}`;
            }
        }
        if (!prefix) {
            prefix = latestOrder.operation === 'buy' ? 'Buy' : 'Sell';
        }
        if (name) {
            return `${prefix} ${name}`;
        }
        return prefix;
    }, [latestOrder, stock?.name]);
    let subtitle = useMemo(() => {
        if ('orderType' in latestOrder &&
            (latestOrder.status === 'pending' || latestOrder.status === 'queued') &&
            (latestOrder.orderType === SellOrderType.LIMIT || latestOrder.orderType === SellOrderType.STOP)) {
            return 'Order will be executed when the market reaches the target price';
        }
        if (latestOrder.status === 'queued' || latestOrder.status === 'cancelled' || latestOrder.status === 'failed') {
            return strings.subtitle[latestOrder.status];
        }
        return undefined;
    }, [latestOrder]);
    if (latestOrder.status === 'queued' || latestOrder.status === 'pending') {
        if (marketOpeningInfo?.status === 'closed' && marketOpeningInfo.nextOpen) {
            subtitle = `${subtitle}${strings.marketOpensAt}${formatDate(marketOpeningInfo.nextOpen)}`;
        }
    }
    if (cancelling) {
        subtitle = strings.cancellingSubtitle;
    }
    const handleRecurringOrder = useCallback(() => {
        if (blockInvestActionOnWeb('Recurring buy'))
            return;
        if (stock?.id) {
            navigation.navigateDeprecated('InvestBuyCustomAmount', {
                assetId: stock.id,
                recurring: true,
            });
        }
    }, [stock?.id]);
    const handleAutoinvestUpsell = useCallback(() => {
        if (!accountType)
            return;
        if (blockInvestActionOnWeb('Add to autoinvest'))
            return;
        if (autoInvestPlan) {
            const existingAllocations = autoInvestPlan.allocations.map((a) => ({
                assetId: a.assetId,
                percentage: a.percentage * 100,
            }));
            navigation.navigate('AutoinvestAllocation', {
                accountType,
                planId: autoInvestPlan.id,
                isEditMode: true,
                isUpsellFlow: true,
                wasPaused: autoInvestPlan.status === 'paused',
                initialAllocations: isAssetInAutoinvest
                    ? existingAllocations
                    : [
                        {
                            assetId: order.assetId,
                            percentage: 0,
                        },
                        ...existingAllocations,
                    ],
            });
        }
        else {
            navigation.navigate('AutoinvestIntro', { accountType });
        }
    }, [accountType, autoInvestPlan, order.assetId, isAssetInAutoinvest]);
    const rows = useMemo(() => {
        const out = {};
        const order = latestOrder;
        // numOfShares & pricePerShare & limitPrice & stopPrice
        if ('orderType' in order && (order.orderType === SellOrderType.LIMIT || order.orderType === SellOrderType.STOP)) {
            const { limitPrice, stopPrice } = order;
            if (order.orderType === SellOrderType.LIMIT && limitPrice) {
                out.limitPrice = { value: limitPrice, currency: 'USD' };
            }
            else if (order.orderType === SellOrderType.STOP && stopPrice) {
                out.stopPrice = { value: stopPrice, currency: 'USD' };
            }
        }
        else if (order.filled) {
            out.pricePerShare = order.filled.price;
        }
        else if ('requested' in order) {
            const { price } = order.requested;
            if (order.status === 'pending' || order.status === 'queued') {
                out.lastClosePrice = price;
            }
            else {
                out.pricePerShare = price;
            }
        }
        // eslint-disable-next-line no-lone-blocks
        {
            let pricing = order.filled || order.estimated;
            if ('requested' in order)
                pricing = pricing || order.requested;
            if ((order.status === 'pending' || order.status === 'queued') && order.operation === 'buy') {
                out.numOfSharesEst = pricing?.quantity;
            }
            else {
                // including filled and cancelled/failed, etc.
                out.numOfShares = pricing?.quantity;
            }
        }
        // fxBaseInfo
        const fxBaseInfo = (() => {
            const { estimated, filled } = order;
            const requested = 'requested' in order ? order.requested : undefined;
            const objectToUse = filled ||
                (estimated &&
                    'fxConversion' in estimated &&
                    estimated.fxConversion.rateFrom &&
                    estimated.fxConversion.rateTo &&
                    estimated.fxConversion.base !== undefined &&
                    estimated.fxConversion.fee?.value !== undefined &&
                    estimated.fxConversion.fee?.currency
                    ? estimated
                    : requested);
            if (objectToUse?.fxConversion) {
                return {
                    hasFilled: !!filled,
                    fxRateBase: {
                        from: { value: 1, currency: objectToUse.fxConversion.rateFrom },
                        to: { value: objectToUse.fxConversion.base, currency: objectToUse.fxConversion.rateTo },
                    },
                    fxFee: objectToUse.fxConversion.fee,
                };
            }
            return undefined;
        })();
        // fxRate & fxCommission
        if (fxBaseInfo?.fxFee) {
            if ('requested' in order) {
                if (fxBaseInfo.hasFilled) {
                    out.fxFee = fxBaseInfo.fxFee;
                }
                else {
                    out.fxFeeEst = fxBaseInfo.fxFee;
                }
                out.fxCommission = order.commission ?? { value: 0, currency: 'GBP' };
            }
        }
        // trading charge
        else {
            const fee = { value: 0, currency: 'GBP' };
            if (isAlpacaAccount) {
                out.alpacaCommission = fee;
            }
            else {
                out.tradingCharge = fee;
            }
        }
        // total amount/cost
        // eslint-disable-next-line no-lone-blocks
        {
            let pricing;
            if ('requested' in order) {
                pricing = order.filled || order.estimated || order.requested;
            }
            else {
                pricing = order.filled || order.estimated;
            }
            if (pricing.amount !== undefined) {
                if (order.operation === 'buy') {
                    out.totalCost = pricing.amount;
                }
                else {
                    const { amount } = pricing;
                    if (order.status === 'pending' || order.status === 'queued') {
                        out.totalAmountEst = amount;
                    }
                    else {
                        out.totalAmount = amount;
                    }
                }
            }
        }
        if ('timeInForce' in order && order.timeInForce && order.operation !== 'buy') {
            out.expireTime = order.timeInForce;
        }
        return out;
    }, [latestOrder, isAlpacaAccount]);
    const [buttons, buttonsHeight] = useMemo(() => {
        const buttons = [];
        let height = HEIGHT_PLUS_PADDING - HEIGHT;
        const isPendingLimitAndStopOrder = 'orderType' in latestOrder &&
            (latestOrder.orderType === SellOrderType.STOP || latestOrder.orderType === SellOrderType.LIMIT) &&
            latestOrder.status === 'pending';
        if ((latestOrder.status === 'queued' || isPendingLimitAndStopOrder) && !cancelling) {
            buttons.push(<Button key="cancel" brandReversed title={strings.cancel} onPress={onCancel}/>);
            height += HEIGHT + rem(8);
        }
        if (isAlpacaAccount && order.operation === 'buy') {
            buttons.push(<Button key="recurring" brandReversed title={strings.recurringOrder} onPress={handleRecurringOrder}/>);
            height += HEIGHT + rem(8);
        }
        const showAutoinvestUpsell = hasAutoInvest &&
            hasFetchedAutoinvestPlans &&
            order.operation === 'buy' &&
            latestOrder.status === 'pending' &&
            !isAssetInAutoinvest;
        buttons.push(<Button key="close" {...(showAutoinvestUpsell ? { brandReversed: true } : { brand: true })} title={strings.close} onPress={onDone}/>);
        if (showAutoinvestUpsell) {
            buttons.push(<Button key="autoinvest" brand title={strings.makeItRecurring} onPress={handleAutoinvestUpsell}/>);
            height += HEIGHT + rem(8);
        }
        height += HEIGHT;
        return [buttons, height];
    }, [
        hasFetchedAutoinvestPlans,
        cancelling,
        handleAutoinvestUpsell,
        handleRecurringOrder,
        hasAutoInvest,
        isAlpacaAccount,
        isAssetInAutoinvest,
        latestOrder,
        onCancel,
        onDone,
        order.operation,
    ]);
    const contentContainerStyle = useMarginBottom('paddingBottom', buttonsHeight, paddingHorizontalStyle, styles.container);
    return (<AppView>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <Receipt status={latestOrder.status} title={title} subtitle={subtitle} logoUrl={stock?.iconUrl} rows={rows}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">{buttons}</ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
// exporting styles for the storybook
export const styleSet = createStyleSheets(() => ({
    container: {
        paddingHorizontal: rem(16),
    },
    buttonsContainer: {
        justifyContent: 'flex-end',
    },
}));
export default OrderScreen;
